import { useState } from 'react'
import { Calendar } from 'primereact/calendar'
import { useTranslation } from 'react-i18next'

/**
 * Paylaşılan **tarih aralığı filtresi** — liste ekranlarının filtre çubuğuna takılır (Reuse Mandate).
 *
 * <p>Projede bu bileşen 058 ile geldi: o güne dek hiçbir liste ekranında tarih aralığı filtresi yoktu
 * (`Calendar` yalnız form/detay ekranlarında kullanılıyordu). Bu yüzden feature klasörüne gömülmedi,
 * paylaşılan katmana kondu — sonraki listeler aynı kalıbı kullanır.
 *
 * <p><b>Aralık her iki uçtan dahildir</b> ve gün sınırlarını **bu bileşen** hesaplar: başlangıç
 * `00:00:00.000`, bitiş `23:59:59.999` — kullanıcının tarayıcı saat diliminde. Sunucuya tam zaman
 * damgası (ISO-8601) gider ve sunucu saat dilimi dönüşümü yapmaz; böylece "31 Temmuz'u seçtim, 31
 * Temmuz'daki kayıt geldi" beklentisi her saat diliminde tutar.
 *
 * <p><b>Doğrulama:</b> bitiş < başlangıç ise alan altında uyarı gösterilir ve {@link onChange}
 * **tetiklenmez** — geçersiz aralık için sunucuya istek gitmez.
 *
 * <p>Anayasa "Uygulama Kabuğu ve Düzen": kontroller 34px, takvim tetik butonu **nötr** ve input'a
 * **bitişik**tir (`ui-daterange` sınıfı bunu sağlar).
 */
export interface DateRangeFilterProps {
  /** Aralık başlangıcı (ISO-8601 instant) veya `null`. */
  from: string | null
  /** Aralık bitişi (ISO-8601 instant) veya `null`. */
  to: string | null
  /** Geçerli bir aralıkta çağrılır. Geçersiz aralıkta **çağrılmaz**. */
  onChange: (range: { from: string | null; to: string | null }) => void
  /**
   * Alan etiketleri **zorunludur**: bileşen paylaşılan katmandadır ve her ekranın kendi i18n
   * anahtarını vermesi beklenir. Varsayılan bir etiket koymak, olmayan bir anahtara sessizce
   * bağlanma riski yaratırdı.
   */
  fromLabel: string
  toLabel: string
}

/** ISO instant → `Calendar`'ın beklediği `Date` (yerel gün). */
function toDate(iso: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Seçilen günün **başlangıcı** (yerel 00:00:00.000) → ISO instant. */
function startOfDay(date: Date | null): string | null {
  if (!date) return null
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/** Seçilen günün **sonu** (yerel 23:59:59.999) → ISO instant; bitiş ucu dahil olsun diye. */
function endOfDay(date: Date | null): string | null {
  if (!date) return null
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

export default function DateRangeFilter({
  from,
  to,
  onChange,
  fromLabel,
  toLabel,
}: Readonly<DateRangeFilterProps>) {
  const { t } = useTranslation()

  /**
   * Kullanıcının **ham seçimi** — yukarı bildirilenden ayrı tutulur.
   *
   * Neden gerekli: geçersiz aralık yukarı bildirilmez (istek atılmasın diye). Seçim yalnız
   * {@link from}/{@link to} prop'larından okunsaydı geçersiz kombinasyon hiç var olamaz, dolayısıyla
   * uyarı **hiçbir zaman görünmezdi** — kullanıcı tarihe tıklar ve hiçbir şey olmazdı. Ham seçimi
   * yerelde tutmak, uyarıyı gösterilebilir kılar (spec FR-030).
   */
  const [draft, setDraft] = useState<{ from: string | null; to: string | null } | null>(null)

  // Dışarıdan gelen değer değiştiyse (ör. "Filtreleri Temizle") taslak bırakılır, prop'lar esas alınır.
  const current = draft ?? { from, to }
  const effectiveFrom = draft ? draft.from : from
  const effectiveTo = draft ? draft.to : to

  const invalid =
    !!effectiveFrom && !!effectiveTo && new Date(effectiveTo) < new Date(effectiveFrom)

  const change = (next: { from: string | null; to: string | null }) => {
    const isInvalid = !!next.from && !!next.to && new Date(next.to) < new Date(next.from)
    if (isInvalid) {
      // Seçimi ekranda GÖSTER (uyarıyla birlikte) ama sunucuya gitme.
      setDraft(next)
      return
    }
    setDraft(null)
    onChange(next)
  }

  return (
    <div className="ui-daterange">
      <Calendar
        value={toDate(current.from)}
        onChange={(e) =>
          change({ from: startOfDay((e.value as Date | null) ?? null), to: current.to })
        }
        dateFormat="dd.mm.yy"
        showIcon
        showButtonBar
        placeholder={fromLabel}
        aria-label={fromLabel}
        className={invalid ? 'p-invalid' : undefined}
      />
      <Calendar
        value={toDate(current.to)}
        onChange={(e) =>
          change({ from: current.from, to: endOfDay((e.value as Date | null) ?? null) })
        }
        dateFormat="dd.mm.yy"
        showIcon
        showButtonBar
        placeholder={toLabel}
        aria-label={toLabel}
        className={invalid ? 'p-invalid' : undefined}
      />
      {invalid && <span className="ui-daterange-error">{t('validation.endBeforeStart')}</span>}
    </div>
  )
}
